import React from 'react';
import { Box } from '@mui/material';
import { tokens } from '../theme';

/**
 * Hand-crafted SVG hero art for blog posts (TASK K4).
 *
 * One motif per post slug, all sharing the same Warm Editorial frame:
 * cream field, hairline rules, ink + copper + sage strokes, soft tints.
 * Scales losslessly (SVG), no stock-photo look, consistent 12:5 crop.
 * `preserveAspectRatio: slice` keeps the composition centered at any size.
 */

const INK = tokens.ink;
const COPPER = tokens.copper;
const COPPER_DARK = tokens.copperDark;
const SAGE = tokens.sage;
const CREAM = tokens.bgDefault;
const BAND = tokens.bandAlt;
const BORDER = tokens.border;
const COPPER_TINT = 'rgba(199, 91, 57, 0.12)';
const SAGE_TINT = 'rgba(47, 93, 80, 0.12)';

/* Shared decorative frame: corner rules + dotted texture */
function Frame() {
  return (
    <g>
      <rect x="0" y="0" width="720" height="300" fill={CREAM} />
      <rect x="0" y="246" width="720" height="54" fill={BAND} />
      <line x1="0" y1="246" x2="720" y2="246" stroke={BORDER} strokeWidth="1" />
      {/* dotted texture, top-left */}
      {[0, 1, 2, 3, 4].map((r) =>
        [0, 1, 2, 3, 4, 5].map((c) => (
          <circle
            key={`${r}-${c}`}
            cx={40 + c * 16}
            cy={36 + r * 16}
            r="1.4"
            fill={COPPER}
            opacity="0.25"
          />
        )),
      )}
      {/* editorial corner rule, bottom-right */}
      <line x1="600" y1="276" x2="688" y2="276" stroke={COPPER} strokeWidth="1.5" opacity="0.6" />
      <line x1="640" y1="284" x2="688" y2="284" stroke={COPPER} strokeWidth="1.5" opacity="0.35" />
    </g>
  );
}

/* ── motifs ──────────────────────────────────────────────────── */

const SpeedClock = (
  <g>
    {/* stopwatch */}
    <circle cx="430" cy="140" r="78" fill="#FFFFFF" stroke={INK} strokeWidth="2.5" />
    <circle cx="430" cy="140" r="64" fill="none" stroke={BORDER} strokeWidth="1.5" />
    <rect x="420" y="48" width="20" height="12" rx="3" fill={INK} />
    {/* 5-minute wedge */}
    <path d="M430 140 L430 84 A56 56 0 0 1 470 100 Z" fill={COPPER_TINT} stroke={COPPER} strokeWidth="1.5" />
    <line x1="430" y1="140" x2="430" y2="88" stroke={COPPER} strokeWidth="3" strokeLinecap="round" />
    <line x1="430" y1="140" x2="466" y2="104" stroke={INK} strokeWidth="3" strokeLinecap="round" />
    <circle cx="430" cy="140" r="5" fill={COPPER} />
    {/* speed lines */}
    <line x1="280" y1="110" x2="330" y2="110" stroke={COPPER} strokeWidth="2.5" strokeLinecap="round" opacity="0.7" />
    <line x1="262" y1="140" x2="326" y2="140" stroke={COPPER} strokeWidth="2.5" strokeLinecap="round" opacity="0.45" />
    <line x1="280" y1="170" x2="330" y2="170" stroke={COPPER} strokeWidth="2.5" strokeLinecap="round" opacity="0.25" />
    {/* winner reply bubble */}
    <rect x="528" y="96" width="120" height="44" rx="12" fill={SAGE_TINT} stroke={SAGE} strokeWidth="1.5" />
    <line x1="546" y1="113" x2="630" y2="113" stroke={SAGE} strokeWidth="2.5" strokeLinecap="round" opacity="0.8" />
    <line x1="546" y1="124" x2="606" y2="124" stroke={SAGE} strokeWidth="2.5" strokeLinecap="round" opacity="0.45" />
    <path d="M548 140 l0 12 12 -12 Z" fill={SAGE_TINT} stroke={SAGE} strokeWidth="1.5" />
  </g>
);

const MissedCalls = (
  <g>
    {/* phone handset */}
    <path
      d="M310 96 q-26 0 -26 26 l0 8 q0 10 10 12 l30 6 q10 2 14 -8 l6 -14 q22 -8 44 0 l6 14 q4 10 14 8 l30 -6 q10 -2 10 -12 l0 -8 q0 -26 -26 -26 q-56 -14 -112 0 Z"
      fill="#FFFFFF"
      stroke={INK}
      strokeWidth="2.5"
      transform="rotate(-8 366 120)"
    />
    {/* missed rings */}
    <path d="M398 70 a 40 40 0 0 1 34 18" stroke={COPPER} strokeWidth="2.5" fill="none" strokeLinecap="round" />
    <path d="M408 50 a 60 60 0 0 1 50 28" stroke={COPPER} strokeWidth="2.5" fill="none" strokeLinecap="round" opacity="0.55" />
    <path d="M418 30 a 80 80 0 0 1 66 38" stroke={COPPER} strokeWidth="2.5" fill="none" strokeLinecap="round" opacity="0.3" />
    {/* leaking coins */}
    <circle cx="500" cy="160" r="16" fill={COPPER_TINT} stroke={COPPER_DARK} strokeWidth="2" />
    <text x="500" y="166" textAnchor="middle" fontSize="16" fontWeight="700" fill={COPPER_DARK} fontFamily="Georgia, serif">$</text>
    <circle cx="546" cy="196" r="13" fill={COPPER_TINT} stroke={COPPER_DARK} strokeWidth="2" opacity="0.75" />
    <text x="546" y="201" textAnchor="middle" fontSize="13" fontWeight="700" fill={COPPER_DARK} fontFamily="Georgia, serif">$</text>
    <circle cx="586" cy="226" r="10" fill={COPPER_TINT} stroke={COPPER_DARK} strokeWidth="2" opacity="0.5" />
    <text x="586" y="230" textAnchor="middle" fontSize="11" fontWeight="700" fill={COPPER_DARK} fontFamily="Georgia, serif">$</text>
  </g>
);

const LeakyFunnel = (
  <g>
    {/* funnel */}
    <path d="M300 70 L520 70 L440 160 L440 220 L380 240 L380 160 Z" fill="#FFFFFF" stroke={INK} strokeWidth="2.5" strokeLinejoin="round" />
    <line x1="316" y1="92" x2="504" y2="92" stroke={BORDER} strokeWidth="1.5" />
    {/* leads entering */}
    <circle cx="340" cy="52" r="7" fill={COPPER} />
    <circle cx="380" cy="44" r="7" fill={COPPER} opacity="0.8" />
    <circle cx="420" cy="50" r="7" fill={COPPER} opacity="0.9" />
    <circle cx="460" cy="42" r="7" fill={COPPER} opacity="0.7" />
    {/* leaking out the sides */}
    <circle cx="350" cy="180" r="6" fill={COPPER} opacity="0.5" />
    <circle cx="330" cy="208" r="5" fill={COPPER} opacity="0.35" />
    <circle cx="476" cy="190" r="6" fill={COPPER} opacity="0.5" />
    <circle cx="496" cy="216" r="5" fill={COPPER} opacity="0.35" />
    {/* the one that converts */}
    <circle cx="410" cy="252" r="9" fill={SAGE} />
    <path d="M405 252 l4 4 l7 -8" stroke="#FFFFFF" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </g>
);

const NightOwl = (
  <g>
    {/* night sky panel */}
    <rect x="270" y="48" width="300" height="170" rx="16" fill={INK} />
    {/* moon */}
    <path d="M512 84 a 26 26 0 1 0 18 44 a 32 32 0 0 1 -18 -44 Z" fill="#FAF7F1" opacity="0.9" />
    {/* stars */}
    <circle cx="318" cy="80" r="2" fill="#FAF7F1" opacity="0.8" />
    <circle cx="356" cy="64" r="1.6" fill="#FAF7F1" opacity="0.6" />
    <circle cx="452" cy="72" r="2" fill="#FAF7F1" opacity="0.7" />
    <circle cx="402" cy="60" r="1.4" fill="#FAF7F1" opacity="0.5" />
    {/* 2 AM chat */}
    <rect x="296" y="116" width="150" height="38" rx="10" fill="rgba(250,247,241,0.12)" stroke="rgba(250,247,241,0.4)" strokeWidth="1.2" />
    <text x="312" y="140" fontSize="15" fill="#FAF7F1" fontFamily="Georgia, serif" fontStyle="italic">2:14 AM — "u open?"</text>
    <rect x="362" y="166" width="178" height="38" rx="10" fill={SAGE_TINT} stroke={tokens.sageLight} strokeWidth="1.2" />
    <text x="378" y="190" fontSize="14" fill="#CFE3DC" fontFamily="Inter, sans-serif">Booked for 7pm tomorrow ✓</text>
  </g>
);

const BotVsAgent = (
  <g>
    {/* rigid bot side */}
    <rect x="282" y="72" width="120" height="120" rx="10" fill="#FFFFFF" stroke={INK} strokeWidth="2" />
    <rect x="306" y="100" width="18" height="10" rx="2" fill={INK} />
    <rect x="358" y="100" width="18" height="10" rx="2" fill={INK} />
    <line x1="312" y1="150" x2="372" y2="150" stroke={INK} strokeWidth="2.5" strokeLinecap="round" />
    <line x1="306" y1="170" x2="330" y2="170" stroke={BORDER} strokeWidth="2" />
    <line x1="338" y1="170" x2="362" y2="170" stroke={BORDER} strokeWidth="2" />
    {/* vs divider */}
    <line x1="430" y1="76" x2="430" y2="196" stroke={BORDER} strokeWidth="1.5" strokeDasharray="5 5" />
    {/* organic agent side — copper spiral */}
    <circle cx="528" cy="132" r="58" fill={COPPER_TINT} />
    <path
      d="M528 92 c 26 0 44 18 44 40 s -18 40 -44 40 s -40 -16 -40 -36 c 0 -16 12 -28 28 -28 c 12 0 22 9 22 21 c 0 8 -6 14 -14 14"
      stroke={COPPER}
      strokeWidth="2.5"
      fill="none"
      strokeLinecap="round"
    />
    <circle cx="564" cy="86" r="4" fill={SAGE} />
    <circle cx="584" cy="106" r="3" fill={SAGE} opacity="0.6" />
  </g>
);

const HouseKeys = (
  <g>
    {/* house */}
    <path d="M300 150 L390 84 L480 150" fill="none" stroke={INK} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M316 142 L316 226 L464 226 L464 142" fill="#FFFFFF" stroke={INK} strokeWidth="2.5" strokeLinejoin="round" />
    <rect x="372" y="170" width="36" height="56" fill={COPPER_TINT} stroke={COPPER} strokeWidth="2" />
    <circle cx="402" cy="198" r="2.5" fill={COPPER_DARK} />
    <rect x="334" y="162" width="24" height="24" fill={SAGE_TINT} stroke={SAGE} strokeWidth="1.5" />
    {/* moon — after-hours showing */}
    <path d="M540 76 a 20 20 0 1 0 14 34 a 25 25 0 0 1 -14 -34 Z" fill={INK} opacity="0.85" />
    {/* key */}
    <circle cx="530" cy="186" r="16" fill="none" stroke={COPPER} strokeWidth="2.5" />
    <line x1="542" y1="197" x2="580" y2="226" stroke={COPPER} strokeWidth="2.5" strokeLinecap="round" />
    <line x1="566" y1="216" x2="574" y2="206" stroke={COPPER} strokeWidth="2.5" strokeLinecap="round" />
  </g>
);

const CartQuestion = (
  <g>
    {/* cart */}
    <path d="M296 92 L322 92 L344 192 L478 192 L500 116 L336 116" fill="none" stroke={INK} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="362" cy="222" r="11" fill="#FFFFFF" stroke={INK} strokeWidth="2.5" />
    <circle cx="456" cy="222" r="11" fill="#FFFFFF" stroke={INK} strokeWidth="2.5" />
    {/* items */}
    <rect x="366" y="136" width="26" height="26" rx="4" fill={COPPER_TINT} stroke={COPPER} strokeWidth="1.5" />
    <rect x="402" y="128" width="26" height="34" rx="4" fill={SAGE_TINT} stroke={SAGE} strokeWidth="1.5" />
    {/* question bubble */}
    <rect x="500" y="64" width="92" height="64" rx="14" fill="#FFFFFF" stroke={COPPER} strokeWidth="2" />
    <path d="M512 128 l-2 18 16 -18 Z" fill="#FFFFFF" stroke={COPPER} strokeWidth="2" strokeLinejoin="round" />
    <text x="546" y="110" textAnchor="middle" fontSize="34" fontWeight="700" fill={COPPER} fontFamily="Georgia, serif">?</text>
  </g>
);

const HealthCalendar = (
  <g>
    {/* calendar */}
    <rect x="300" y="76" width="180" height="150" rx="12" fill="#FFFFFF" stroke={INK} strokeWidth="2.5" />
    <line x1="300" y1="112" x2="480" y2="112" stroke={INK} strokeWidth="2.5" />
    <line x1="336" y1="62" x2="336" y2="90" stroke={INK} strokeWidth="2.5" strokeLinecap="round" />
    <line x1="444" y1="62" x2="444" y2="90" stroke={INK} strokeWidth="2.5" strokeLinecap="round" />
    {/* grid dots */}
    {[0, 1, 2].map((r) =>
      [0, 1, 2, 3].map((c) => (
        <rect
          key={`hc-${r}-${c}`}
          x={318 + c * 38}
          y={126 + r * 30}
          width="22"
          height="18"
          rx="4"
          fill={r === 1 && c === 2 ? COPPER_TINT : CREAM}
          stroke={r === 1 && c === 2 ? COPPER : BORDER}
          strokeWidth="1.2"
        />
      )),
    )}
    {/* medical cross */}
    <circle cx="540" cy="120" r="34" fill={SAGE_TINT} stroke={SAGE} strokeWidth="2" />
    <rect x="532" y="102" width="16" height="36" rx="3" fill={SAGE} />
    <rect x="522" y="112" width="36" height="16" rx="3" fill={SAGE} />
    {/* confirmed slot line */}
    <path d="M520 180 q 30 24 60 4" stroke={COPPER} strokeWidth="2" fill="none" strokeDasharray="4 4" />
  </g>
);

const TrainingBrain = (
  <g>
    {/* documents */}
    <rect x="282" y="84" width="74" height="94" rx="8" fill="#FFFFFF" stroke={INK} strokeWidth="2" transform="rotate(-6 319 131)" />
    <rect x="306" y="96" width="74" height="94" rx="8" fill="#FFFFFF" stroke={INK} strokeWidth="2" />
    <line x1="320" y1="118" x2="364" y2="118" stroke={BORDER} strokeWidth="2.5" strokeLinecap="round" />
    <line x1="320" y1="134" x2="364" y2="134" stroke={BORDER} strokeWidth="2.5" strokeLinecap="round" />
    <line x1="320" y1="150" x2="350" y2="150" stroke={BORDER} strokeWidth="2.5" strokeLinecap="round" />
    {/* flow arrow */}
    <path d="M392 142 C 430 142, 430 142, 458 142" stroke={COPPER} strokeWidth="2" strokeDasharray="5 4" fill="none" />
    <polygon points="456,136 470,142 456,148" fill={COPPER} />
    {/* spark / brain */}
    <circle cx="540" cy="142" r="52" fill={COPPER_TINT} />
    <path
      d="M540 104 c 22 0 38 16 38 38 s -16 38 -38 38 s -36 -14 -36 -32 c 0 -14 11 -25 25 -25 c 11 0 19 8 19 18 c 0 7 -5 12 -12 12"
      stroke={COPPER}
      strokeWidth="2.5"
      fill="none"
      strokeLinecap="round"
    />
    {/* 15-min badge */}
    <circle cx="592" cy="96" r="22" fill="#FFFFFF" stroke={SAGE} strokeWidth="2" />
    <text x="592" y="93" textAnchor="middle" fontSize="12" fontWeight="700" fill={SAGE} fontFamily="Inter, sans-serif">15</text>
    <text x="592" y="106" textAnchor="middle" fontSize="9" fontWeight="600" fill={SAGE} fontFamily="Inter, sans-serif">MIN</text>
  </g>
);

const RoiChart = (
  <g>
    {/* axes */}
    <line x1="300" y1="70" x2="300" y2="226" stroke={INK} strokeWidth="2" strokeLinecap="round" />
    <line x1="300" y1="226" x2="580" y2="226" stroke={INK} strokeWidth="2" strokeLinecap="round" />
    {/* bars */}
    <rect x="328" y="186" width="42" height="40" rx="4" fill={BAND} stroke={BORDER} strokeWidth="1.5" />
    <rect x="392" y="158" width="42" height="68" rx="4" fill={COPPER_TINT} stroke={COPPER} strokeWidth="1.5" />
    <rect x="456" y="118" width="42" height="108" rx="4" fill={COPPER_TINT} stroke={COPPER} strokeWidth="2" />
    <rect x="520" y="84" width="42" height="142" rx="4" fill={SAGE_TINT} stroke={SAGE} strokeWidth="2" />
    {/* trend line */}
    <path d="M349 176 L413 148 L477 108 L541 74" stroke={SAGE} strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="1 0" />
    <circle cx="541" cy="74" r="6" fill={SAGE} />
    {/* coin */}
    <circle cx="600" cy="96" r="18" fill={COPPER_TINT} stroke={COPPER_DARK} strokeWidth="2" />
    <text x="600" y="103" textAnchor="middle" fontSize="18" fontWeight="700" fill={COPPER_DARK} fontFamily="Georgia, serif">$</text>
  </g>
);

const DefaultMotif = (
  <g>
    <circle cx="430" cy="140" r="64" fill="none" stroke={INK} strokeWidth="2.5" />
    <path
      d="M430 100 c 24 0 40 17 40 40 s -16 40 -40 40 s -38 -15 -38 -34 c 0 -15 12 -27 27 -27 c 12 0 21 9 21 20 c 0 8 -6 13 -13 13"
      stroke={COPPER}
      strokeWidth="2.5"
      fill="none"
      strokeLinecap="round"
    />
    <circle cx="510" cy="92" r="5" fill={SAGE} />
  </g>
);

const motifs: Record<string, React.ReactNode> = {
  'why-78-percent-customers-buy-from-first-responder': SpeedClock,
  'hidden-cost-of-missed-calls-10k-monthly': MissedCalls,
  '73-percent-leads-never-contacted': LeakyFunnel,
  'after-hours-inquiries-convert-better': NightOwl,
  'chatbots-vs-ai-agents-difference': BotVsAgent,
  'real-estate-after-hours-leads': HouseKeys,
  'ecommerce-cart-abandonment-unanswered-questions': CartQuestion,
  'healthcare-scheduling-hassles-patient-loss': HealthCalendar,
  'train-ai-on-your-business-15-minutes': TrainingBrain,
  'roi-calculator-ai-customer-engagement': RoiChart,
};

interface BlogHeroArtProps {
  slug: string;
  title: string;
  /** Rendered height; SVG slices to fill the width (consistent crop). */
  height?: number | string | Record<string, number | string>;
  rounded?: boolean;
}

export default function BlogHeroArt({ slug, title, height = 240, rounded = false }: BlogHeroArtProps) {
  const motif = motifs[slug] ?? DefaultMotif;
  return (
    <Box
      role="img"
      aria-label={`Illustration: ${title}`}
      sx={{
        width: '100%',
        height,
        overflow: 'hidden',
        borderRadius: rounded ? `${tokens.radiusCard}px` : 0,
        lineHeight: 0,
        '& svg': { width: '100%', height: '100%', display: 'block' },
      }}
    >
      <svg viewBox="0 0 720 300" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
        <Frame />
        {motif}
      </svg>
    </Box>
  );
}
